import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../context/AdminContext';
import { 
  Search, 
  Edit, 
  Trash2, 
  UserPlus,
  Download,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Card from '../../components/common/Card';

const UserManagement = () => {
  const { t } = useTranslation();
  const { 
    users, 
    loading, 
    fetchUsers, 
    deleteUser,
    fetchPlans
  } = useAdmin();

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [enabledFilter, setEnabledFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  // Removed unused showEditModal state
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pagination, setPagination] = useState({});

  const loadUsers = useCallback(async () => {
    const filters = {
      search: searchTerm,
      role: roleFilter !== 'all' ? roleFilter : undefined,
      enabled: enabledFilter !== 'all' ? enabledFilter === 'enabled' : undefined
    };
    
    const result = await fetchUsers(currentPage, 20, filters);
    if (result?.pagination) {
      setPagination(result.pagination);
    }
  }, [currentPage, roleFilter, enabledFilter, searchTerm, fetchUsers]);

  useEffect(() => {
    loadUsers();
    fetchPlans();
  }, [loadUsers, fetchPlans]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRoleFilter = (role) => {
    setRoleFilter(role);
    setCurrentPage(1);
  };

  const handleEnabledFilter = (enabled) => {
    setEnabledFilter(enabled);
    setCurrentPage(1);
  };

  const handleEditUser = (user) => {
    navigate(`/admin/users/${user.id}`);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Removed unused handleSaveUser (edit handled via separate detail page now)

  const handleConfirmDelete = async () => {
    try {
      const result = await deleteUser(selectedUser?.id);
      if (result?.success) {
        toast.success(t('admin.userManagement.toasts.userDeleted', 'User deleted successfully'));
        setShowDeleteModal(false);
        setSelectedUser(null);
        loadUsers();
      } else {
        toast.error(result?.error || t('admin.userManagement.errors.deleteFailed', 'Failed to delete user'));
      }
    } catch (e) {
      toast.error(t('admin.userManagement.errors.deleteFailed', 'Failed to delete user'));
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'ADMIN': return 'bg-red-600';
      case 'OWNER': return 'bg-blue-600';
      case 'BARISTA': return 'bg-green-600';
      case 'CUSTOMER': return 'bg-purple-600';
      default: return 'bg-gray-600';
    }
  };

  if (loading && !users.length) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">{t('admin.userManagement.loadingUsers')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{t('admin.userManagement.title')}</h1>
            <p className="text-gray-400 text-sm md:text-base">{t('admin.userManagement.description')}</p>
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <Button variant="secondary" size="sm">
              <Download className="w-4 h-4 mr-2" />
              {t('admin.userManagement.export')}
            </Button>
            <Button size="sm" className="flex-1 sm:flex-none">
              <UserPlus className="w-4 h-4 mr-2" />
              {t('admin.userManagement.addUser')}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder={t('admin.userManagement.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearch}
                size="sm"
                className="pl-10"
              />
            </div>
            
            <Select
              value={roleFilter}
              onChange={(e) => handleRoleFilter(e.target.value)}
              size="sm"
            >
              <option value="all">{t('admin.userManagement.allRoles')}</option>
              <option value="ADMIN">{t('admin.userManagement.roles.admin')}</option>
              <option value="OWNER">{t('admin.userManagement.roles.owner')}</option>
              <option value="BARISTA">{t('admin.userManagement.roles.barista')}</option>
              <option value="CUSTOMER">{t('admin.userManagement.roles.customer')}</option>
            </Select>

            <Select
              value={enabledFilter}
              onChange={(e) => handleEnabledFilter(e.target.value)}
              size="sm"
            >
              <option value="all">{t('admin.userManagement.allStatus')}</option>
              <option value="enabled">{t('admin.userManagement.status.enabled')}</option>
              <option value="disabled">{t('admin.userManagement.status.disabled')}</option>
            </Select>

            <Button
              variant="secondary"
              size="sm"
              onClick={loadUsers}
              className="w-full md:w-auto"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('admin.userManagement.refresh')}
            </Button>
          </div>
        </Card>

        {/* Users Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('admin.userManagement.tableHeaders.user')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('admin.userManagement.tableHeaders.role')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('admin.userManagement.tableHeaders.enabled')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('admin.userManagement.tableHeaders.shop')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('admin.userManagement.tableHeaders.joined')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('admin.userManagement.tableHeaders.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(users) && users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-gray-600 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <span className="text-white font-medium text-sm">
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-medium text-sm md:text-base truncate">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-gray-400 text-xs md:text-sm truncate">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getRoleBadgeColor(user.role)}`}>
                        {user.role ? t(`admin.userManagement.roles.${user.role.toLowerCase()}`) : t('admin.userManagement.roles.user')}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${user.enabled ? 'bg-green-600' : 'bg-red-600'}`}>
                        {user.enabled ? t('admin.userManagement.status.enabled') : t('admin.userManagement.status.disabled')}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-white text-sm truncate">{user.shopName || user.shop?.name || t('admin.userManagement.noShop')}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-gray-400 text-sm">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => handleEditUser(user)}
                          className="p-2"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => handleDeleteUser(user)}
                          className="p-2 text-red-400 hover:bg-red-400/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-6 border-t border-gray-700">
              <p className="text-gray-400 text-sm">
                {t('admin.userManagement.pagination.showing', {
                  start: pagination.startItem,
                  end: pagination.endItem,
                  total: pagination.totalItems
                })}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t('admin.userManagement.pagination.previous')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                >
                  {t('admin.userManagement.pagination.next')}
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Edit User Modal replaced by full-page view */}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedUser && (
          <DeleteUserModal
            user={selectedUser}
            onConfirm={handleConfirmDelete}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedUser(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Edit User Modal removed in favor of full-page UserDetail

// Delete Confirmation Modal Component
const DeleteUserModal = ({ user, onConfirm, onClose }) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-bold text-white mb-4">{t('admin.userManagement.deleteModal.title')}</h3>
        <p className="text-gray-400 mb-6">
          {t('admin.userManagement.deleteModal.message', {
            userName: `${user.firstName} ${user.lastName}`
          })}
        </p>
        <div className="flex space-x-3">
          <button onClick={onConfirm} className="btn-danger flex-1">
            {t('admin.userManagement.deleteModal.confirmDelete')}
          </button>
          <button onClick={onClose} className="btn-secondary flex-1">
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
